// / <reference path="../helpers/credentials.d.ts" />
import {
  useLoginMutationMutation,
  useNewRegisterMutationMutation,
  RegisterResult, useActivateSecondFlowMutation,
} from 'queries';
import { useState } from 'react';
import useTokenManager from 'hooks/useTokenManager';
import useQueryParams from 'hooks/useQueryParams';
import useRouter from 'use-react-router';
import { isUrlRelative } from '../helpers';

export type LoginError = {
  message: string;
  field: 'email' | 'password';
};

function makeError(message: string, field: 'email' | 'password' = 'email'): LoginError {
  return { field, message };
}

function storeCredentials({ email, password }): Promise<CredentialType | null> {
  if ((window as any).PasswordCredential) {
    // eslint-disable-next-line no-undef
    const credentials = new PasswordCredential({
      id: email,
      name: email,
      password,
    });

    console.log('storing credentials', credentials);

    return navigator.credentials.store(credentials);
  }

  return null;
}

export function useLoginHelper(isRegister = true) {
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);

  const tokenManager = useTokenManager();
  const params = useQueryParams();

  const { history } = useRouter();
  const [loginMutation] = useLoginMutationMutation();
  const [registerMutation] = useNewRegisterMutationMutation();
  const [activateSecondFlow] = useActivateSecondFlowMutation();

  const usedMutation = isRegister ? registerMutation : loginMutation;

  const redirect = async () => {
    const queryBackUrl = Array.isArray(params.backUrl)
      ? params.backUrl[0]
      : params.backUrl || '';
    const backUrl = isUrlRelative(queryBackUrl) ? queryBackUrl : '/tasks';

    history.push(backUrl);
  };

  const setJwtToken = async jwtToken => {
    const wasLoading = isLoading;

    if (!wasLoading) {
      setLoading(true);
    }

    // Thing is, if we set token immediately race between fetching user and activating second flow happens.
    // Therefore, second flow shall be activated and only then apollo should fetch user data.

    // We should initialize second flow and only then set token to let app reflect changes
    tokenManager.setToken(jwtToken, !isRegister);

    if (isRegister) {
      const { data } = await activateSecondFlow({
        context: {
          headers: {
            authorization: `Bearer ${jwtToken}`,
          },
        },
      });

      tokenManager.setToken(jwtToken);

      if (data && data.activateSecondFlow.isSuccess) {
        history.push('/second-flow');
      }
    }

    if (!wasLoading) {
      setLoading(false);
    }

    return true;
  };

  // Handle response returned by GraphQL server
  // Returns true or false depending on whether it's success or not
  const handleResponse = (response: RegisterResult) => {
    const {
      isSuccess, errorCode, jwtToken, errorMessage,
    } = response;

    if (jwtToken) {
      return setJwtToken(jwtToken);
    }

    if (errorCode) {
      setError(
        makeError(
          errorMessage.message,
          errorCode === 'invalidLoginPassword' ? 'password' : 'email',
        ),
      );
    } else {
      setError(makeError('Unknown internal error'));
    }

    return false;
  };

  const submit = async ({ email, password }) => {
    setError(null);
    setLoading(true);

    const mutationResult = await usedMutation({
      variables: { email, password },
    });

    if (mutationResult.data) {
      const result = await handleResponse(mutationResult.data.result);
      if (result) {
        storeCredentials({ email, password });
        await redirect();
      }

      setLoading(false);

      return;
    }

    setLoading(false);
    setError(makeError('Unknown internal error'));
  };

  const getFromCredentials = async () => {
    if (navigator && navigator.credentials) {
      const credentials = await navigator.credentials.get({ password: true, mediation: 'optional' });
      await submit({ email: credentials.id, password: (credentials as any).password });
    }
  };

  return {
    setJwtToken,
    setLoading,
    isLoading,
    error,
    submit,
    redirect,
    getFromCredentials,
  };
}
